import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../config';
import { fetchAndroidTvDevices, AndroidTvDevice } from './androidtvApi';
import { eventBus } from './EventBus';
import { patchRoomCardLive } from '../utils/roomCardIdentity';
import { createRoomMembership } from '../utils/roomMembership';

export const mapaAndroidTv: Record<number, AndroidTvDevice> = {};
let loaded = false;
let socket: Socket | null = null;
/** Un solo GET a la vez: un refresco vacío o atrasado no pisa al que ya mostró las tarjetas. */
let refreshInFlight: Promise<AndroidTvDevice[]> | null = null;

/** Tras power on/off el STB tarda en reportar is_on; no pisar el estado esperado. */
const powerOverrideUntil: Record<number, { is_on: boolean; until: number }> = {};
const POWER_OVERRIDE_MS = 35000;

export function isMapaAndroidTvCargado(): boolean {
  return loaded;
}

const membership = createRoomMembership<AndroidTvDevice>();

export function getAndroidTvByHabitacion(habitacionId: number): AndroidTvDevice[] {
  return membership.cardsInRoom(habitacionId, mapaAndroidTv);
}

/** La habitación que está abierta: este id sigue siendo de esa habitación. */
export function ensureAndroidTvInRoom(
  roomId: number,
  device: Partial<AndroidTvDevice> & { id: number },
): void {
  const id = Number(device.id);
  if (!membership.ensureInRoom(roomId, { ...device, id })) return;
  const locked = membership.cardsInRoom(roomId, mapaAndroidTv).find((d) => Number(d.id) === id);
  const prev = mapaAndroidTv[id];
  if (locked) {
    const next = { ...(prev || locked), ...locked, id } as AndroidTvDevice;
    applyPowerOverride(id, device, next);
    mapaAndroidTv[id] = next;
  }
  eventBus.emit('androidtv:updated');
}

export function unlockAndroidTvRoom(id: number): void {
  membership.unlock(id);
}

function applyPowerOverride(id: number, incoming: Partial<AndroidTvDevice>, next: AndroidTvDevice): void {
  const ov = powerOverrideUntil[id];
  if (!ov || Date.now() >= ov.until) return;
  if (incoming.is_on === ov.is_on) {
    delete powerOverrideUntil[id];
  } else if (incoming.is_on !== undefined && incoming.is_on !== null) {
    next.is_on = ov.is_on;
  }
}

/** Lista completa del servidor: actualiza los que vienen, no borra los que faltan. */
function mergeList(devices: AndroidTvDevice[]) {
  let changed = false;
  devices.forEach((d) => {
    if (!d?.id) return;
    const id = Number(d.id);
    const next = { ...(mapaAndroidTv[id] || {}), ...d, id } as AndroidTvDevice;
    applyPowerOverride(id, d, next);
    mapaAndroidTv[id] = next;
    membership.lockFromList(next);
    changed = true;
  });
  if (changed) eventBus.emit('androidtv:updated');
}

/**
 * Estado en vivo. No pisa habitación ni enabled: un refresh de la habitación
 * no puede hacer desaparecer el decodificador hasta recargar la app.
 */
function mergeLive(devices: Array<Partial<AndroidTvDevice> & { id?: number }>) {
  let changed = false;
  devices.forEach((d) => {
    const id = Number(d?.id);
    if (!Number.isFinite(id)) return;
    const next = patchRoomCardLive(mapaAndroidTv[id], { ...d, id });
    if (!next) return;
    applyPowerOverride(id, d, next);
    mapaAndroidTv[id] = next;
    changed = true;
  });
  if (changed) eventBus.emit('androidtv:updated');
}

/** Actualiza el mapa local (respuesta de control / refresh). */
export function patchAndroidTvDevice(device: AndroidTvDevice): void {
  if (!device?.id) return;
  mergeLive([device]);
}

/** Fuerza is_on inmediato tras power (animación + UI al instante). */
export function setAndroidTvPowerOptimistic(deviceId: number, isOn: boolean): void {
  powerOverrideUntil[deviceId] = { is_on: isOn, until: Date.now() + POWER_OVERRIDE_MS };
  const existing = mapaAndroidTv[deviceId];
  if (existing) {
    mapaAndroidTv[deviceId] = { ...existing, is_on: isOn };
    eventBus.emit('androidtv:updated');
  }
}

async function refrescarAndroidTvNow(): Promise<AndroidTvDevice[]> {
  try {
    const list = await fetchAndroidTvDevices();
    if (!Array.isArray(list)) return Object.values(mapaAndroidTv);
    if (list.length === 0 && Object.keys(mapaAndroidTv).length > 0) {
      loaded = true;
      return Object.values(mapaAndroidTv);
    }
    mergeList(list);
    loaded = true;
    return Object.values(mapaAndroidTv);
  } catch {
    loaded = true;
    return Object.values(mapaAndroidTv);
  }
}

export function refrescarAndroidTv(): Promise<AndroidTvDevice[]> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refrescarAndroidTvNow().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function cargarAndroidTv(): Promise<AndroidTvDevice[]> {
  ensureSocket();
  return refrescarAndroidTv();
}

function ensureSocket() {
  if (socket) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }
  socket = io(serverUrl, {
    path: '/socket.io-androidtv',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
  });
  socket.on('androidtv_status', (data: Partial<AndroidTvDevice> & { id?: number }) => {
    if (!data?.id) return;
    const id = Number(data.id);
    const existing = mapaAndroidTv[id];
    if (!existing) {
      void refrescarAndroidTv();
      return;
    }
    mergeLive([{ ...data, id }]);
  });
}
